/**
 * Client-side rate limiting for public routes to reduce scraping and abuse.
 * Uses sessionStorage so limits are per-tab/session. Not a substitute for server-side limits.
 */

const PREFIX = 'gradedplay_ratelimit_';

function getState(key) {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setState(key, state) {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(state));
  } catch (_) {}
}

/**
 * Check and consume one slot if allowed.
 * @param {string} key - e.g. 'landing_fetch', 'login_attempt'
 * @param {{ maxCount: number, windowMs: number }} options - default { maxCount: 10, windowMs: 60000 }
 * @returns {{ allowed: boolean, retryAfterMs: number }}
 */
export function checkRateLimit(key, options = {}) {
  const { maxCount = 10, windowMs = 60000 } = options;
  const now = Date.now();
  let state = getState(key) || { count: 0, windowStart: now };

  if (now - state.windowStart >= windowMs) {
    state = { count: 0, windowStart: now };
  }

  if (state.count >= maxCount) {
    const windowEnd = state.windowStart + windowMs;
    const retryAfterMs = Math.max(0, windowEnd - now);
    return { allowed: false, retryAfterMs };
  }

  state.count += 1;
  setState(key, state);
  return { allowed: true, retryAfterMs: 0 };
}

/**
 * Check without consuming a slot (e.g. to show "try again in Xs" without counting).
 */
export function peekRateLimit(key, options = {}) {
  const { maxCount = 10, windowMs = 60000 } = options;
  const now = Date.now();
  const state = getState(key) || { count: 0, windowStart: now };

  if (now - state.windowStart >= windowMs) {
    return { allowed: true, retryAfterMs: 0 };
  }

  if (state.count >= maxCount) {
    const windowEnd = state.windowStart + windowMs;
    const retryAfterMs = Math.max(0, windowEnd - now);
    return { allowed: false, retryAfterMs };
  }

  return { allowed: true, retryAfterMs: 0 };
}

/**
 * Check multiple rate-limit tiers; allow only if all pass, then consume one slot on each.
 * Use for stronger limiting (e.g. 10/min AND 25/5min).
 * @param {{ key: string, options: { maxCount: number, windowMs: number } }[]} tiers
 * @returns {{ allowed: boolean, retryAfterMs: number }}
 */
export function checkRateLimitTiered(tiers) {
  if (!tiers.length) return { allowed: true, retryAfterMs: 0 };
  let maxRetryMs = 0;
  for (const { key, options } of tiers) {
    const { allowed, retryAfterMs } = peekRateLimit(key, options);
    if (!allowed) {
      return { allowed: false, retryAfterMs: Math.max(maxRetryMs, retryAfterMs) };
    }
    maxRetryMs = Math.max(maxRetryMs, retryAfterMs);
  }
  for (const { key, options } of tiers) {
    checkRateLimit(key, options);
  }
  return { allowed: true, retryAfterMs: 0 };
}

/** Keys used by the app. */
export const RATE_LIMIT_KEYS = {
  LANDING_PAGE_VIEW: 'landing_page_view',
  LANDING_PAGE_VIEW_5M: 'landing_page_view_5m',
  LANDING_FETCH: 'landing_fetch',
  LANDING_FETCH_5M: 'landing_fetch_5m',
  LOGIN_PAGE_VIEW: 'login_page_view',
  LOGIN_PAGE_VIEW_5M: 'login_page_view_5m',
  LOGIN_ATTEMPT: 'login_attempt',
  LOGIN_ATTEMPT_5M: 'login_attempt_5m',
  LOGIN_ATTEMPT_1D: 'login_attempt_1d',
};

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Stricter preset: short window + 5-min cap for public routes. */
export const RATE_LIMIT_PRESETS = {
  LANDING_PAGE_VIEW: [
    { key: RATE_LIMIT_KEYS.LANDING_PAGE_VIEW, options: { maxCount: 8, windowMs: 60000 } },
    { key: RATE_LIMIT_KEYS.LANDING_PAGE_VIEW_5M, options: { maxCount: 20, windowMs: 300000 } },
  ],
  LANDING_FETCH: [
    { key: RATE_LIMIT_KEYS.LANDING_FETCH, options: { maxCount: 5, windowMs: 60000 } },
    { key: RATE_LIMIT_KEYS.LANDING_FETCH_5M, options: { maxCount: 15, windowMs: 300000 } },
  ],
  LOGIN_PAGE_VIEW: [
    { key: RATE_LIMIT_KEYS.LOGIN_PAGE_VIEW, options: { maxCount: 1, windowMs: 60000 } },
    { key: RATE_LIMIT_KEYS.LOGIN_PAGE_VIEW_5M, options: { maxCount: 15, windowMs: 300000 } },
  ],
  /** Sign-in attempts: 3/min, 8/5min, 100/day. */
  LOGIN_ATTEMPT: [
    { key: RATE_LIMIT_KEYS.LOGIN_ATTEMPT, options: { maxCount: 3, windowMs: 60000 } },
    { key: RATE_LIMIT_KEYS.LOGIN_ATTEMPT_5M, options: { maxCount: 8, windowMs: 300000 } },
    { key: RATE_LIMIT_KEYS.LOGIN_ATTEMPT_1D, options: { maxCount: 100, windowMs: ONE_DAY_MS } },
  ],
};
